/**
 * A plain text PDF, written here (no library): a title, a muted sub line, then lines of text,
 * Helvetica on US Letter, paged as needed. Used where a page offers "PDF" for what it already
 * shows (Performance's QBR and portfolio), so the file is made in the browser and saved
 * straight away, with no print window to be blocked.
 */

/** Characters outside WinAnsi become a close ASCII stand-in, so nothing prints as junk. */
const WIN: Record<string, number> = { '€': 0x80, '‚': 0x82, '„': 0x84, '…': 0x85, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97, '™': 0x99, '·': 0xb7, '×': 0xd7, '÷': 0xf7 };

function winAnsi(s: string): number[] {
  const out: number[] = [];
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (WIN[ch] !== undefined) out.push(WIN[ch]);
    else if (c < 0x80 || (c >= 0xa0 && c <= 0xff)) out.push(c);
    else if (ch === '▲') out.push(0x5e);
    else if (ch === '▼') out.push(0x76);
    else out.push(0x3f);
  }
  return out;
}

/** A PDF string literal from bytes: ( ) \ escaped, high bytes as octal. */
function lit(bytes: number[]): string {
  let s = '(';
  for (const b of bytes) {
    if (b === 0x28 || b === 0x29 || b === 0x5c) s += '\\' + String.fromCharCode(b);
    else if (b < 0x20 || b > 0x7e) s += '\\' + b.toString(8).padStart(3, '0');
    else s += String.fromCharCode(b);
  }
  return s + ')';
}

/** Wraps a line to about `max` characters at spaces (Helvetica at 9.5pt fits ~110 on a page). */
export function wrap(line: string, max: number): string[] {
  if (line.length <= max) return [line];
  const out: string[] = [];
  let cur = '';
  for (const w of line.split(' ')) {
    if ((cur + ' ' + w).trim().length > max && cur) {
      out.push(cur);
      cur = w;
    } else cur = (cur + ' ' + w).trim();
  }
  if (cur) out.push(cur);
  return out;
}

export function textPdf(title: string, sub: string, lines: string[]): Blob {
  const W = 612;
  const H = 792;
  const M = 48;
  const body = lines.flatMap((l) => (l === '' ? [''] : wrap(l, 105)));
  const perPage = Math.floor((H - 2 * M - 46) / 13);
  const pages: string[][] = [];
  for (let i = 0; i < Math.max(1, body.length); i += perPage) pages.push(body.slice(i, i + perPage));

  const objs: string[] = [];
  // 1 catalog, 2 pages, 3 font regular, 4 font bold, then per page: page, content.
  const kids = pages.map((_p, i) => `${5 + i * 2} 0 R`).join(' ');
  objs[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objs[2] = `<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`;
  objs[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
  objs[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';
  pages.forEach((pl, i) => {
    let y = H - M;
    let c = 'BT\n';
    if (i === 0) {
      c += `/F2 15 Tf 1 0 0 1 ${M} ${y} Tm ${lit(winAnsi(title))} Tj\n`;
      y -= 18;
      c += `/F1 9 Tf 0.33 0.38 0.42 rg 1 0 0 1 ${M} ${y} Tm ${lit(winAnsi(sub))} Tj 0 0 0 rg\n`;
      y -= 28;
    } else y -= 6;
    c += '/F1 9.5 Tf\n';
    for (const l of pl) {
      c += `1 0 0 1 ${M} ${y} Tm ${lit(winAnsi(l))} Tj\n`;
      y -= 13;
    }
    c += `/F1 8 Tf 0.33 0.38 0.42 rg 1 0 0 1 ${W - M - 40} ${M / 2} Tm ${lit(winAnsi(`${i + 1} of ${pages.length}`))} Tj\nET`;
    objs[5 + i * 2] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${6 + i * 2} 0 R >>`;
    objs[6 + i * 2] = `<< /Length ${c.length} >>\nstream\n${c}\nendstream`;
  });

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  for (let n = 1; n < objs.length; n++) {
    offsets[n] = pdf.length;
    pdf += `${n} 0 obj\n${objs[n]}\nendobj\n`;
  }
  const xref = pdf.length;
  pdf += `xref\n0 ${objs.length}\n0000000000 65535 f \n`;
  for (let n = 1; n < objs.length; n++) pdf += `${String(offsets[n]).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objs.length} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  // Every character above is one byte (the text went through winAnsi + octal escapes).
  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: 'application/pdf' });
}

/** Saves a blob as a file download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
